import { FC } from 'react';

/** 발행 확인 대상 — 화면마다 쿠폰 타입이 달라 필요한 값만 받는다 */
export interface IssueLinkConfirmTarget {
  couponId: string;
  type: 'refund' | 'renewal';
  /** "#3 · ABC123" 식 표기 */
  label?: string;
}

interface IssueLinkConfirmModalProps {
  target: IssueLinkConfirmTarget;
  /** 발행 뒤 안내 문자 모달로 이어지는지 (환불 링크 + 관리자) */
  followsNotice?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const COPY = {
  refund: {
    title: '환불 링크 발행',
    effect: '고객이 환불 신청 페이지를 열 수 있게 됩니다.',
    tone: 'text-orange-800',
    button: 'bg-orange-600 hover:bg-orange-700',
  },
  renewal: {
    title: '연장 링크 발행',
    effect: '고객이 연장 신청 페이지를 열 수 있게 됩니다.',
    tone: 'text-violet-800',
    button: 'bg-violet-600 hover:bg-violet-700',
  },
} as const;

/**
 * 환불/연장 링크 발행 확인 모달.
 * 버튼 한 번에 바로 발행되면 오클릭으로 고객 신청 페이지가 열리므로 한 번 확인을 거친다.
 */
export const IssueLinkConfirmModal: FC<IssueLinkConfirmModalProps> = ({
  target,
  followsNotice,
  onConfirm,
  onCancel,
}) => {
  const copy = COPY[target.type];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative flex w-full max-w-sm flex-col gap-4 rounded-xl bg-white p-5 shadow-2xl">
        <div>
          <h3 className={`text-base font-bold ${copy.tone}`}>{copy.title}</h3>
          {target.label && (
            <p className="mt-0.5 text-xs font-medium text-gray-500">{target.label}</p>
          )}
        </div>

        <ul className="list-disc space-y-1 pl-4 text-sm text-gray-700">
          <li>{copy.effect}</li>
          <li>링크는 클립보드에 복사됩니다.</li>
          {followsNotice && <li>발행 후 안내 문자 발송으로 이어집니다.</li>}
          <li>고객 신청 전에는 발행을 취소할 수 있습니다.</li>
        </ul>

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-bold text-white ${copy.button}`}
          >
            발행
          </button>
        </div>
      </div>
    </div>
  );
};
